/* istanbul ignore file */

/* eslint-disable max-len */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import BaseTemperatureChannel
  from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import { CHANNEL_TYPE_ORDER, getReadingCardComponent } from '../../common/channelGroupTypeToComponentMap';
import {
  adjustFromDisplay,
  normalizeRaaChannelKey,
  parseChannelKey,
  trimRaaChannelKey,
} from '../../../../../../../utility/channelUtils';
import { CrudStatus as AvtechApiStatus } from '../../../../../../../utility/types/CrudStatus';
import {
  fetchSingleDevice,
  resetDeviceDataFormState,
  selectSensorUpdateStatus,
  updateSensor,
} from '../../../../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import sensorSettingsModalFactory from '../../../../modals/settings/sensorSettingsModalFactory';
import { selectSessionInformation } from '../../../../../../../redux/slices/sessionSlice';
import formatTimestamp from '../../../../../../../utility/formatTimestamp';

const DeviceSensorCard = ({
  channelGroup, lastReceived, isHidden, isMuted, isReadOnly,
}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = React.useState(false);
  const { temperatureScale, timezone, activeSubscriptionId } = useSelector(selectSessionInformation);
  const preferencesDisplay = { temperatureScale };
  const saveStatus = useSelector(selectSensorUpdateStatus);
  const parsedChannelKey = parseChannelKey(channelGroup.channelKey);
  const ReadingCardComponent = getReadingCardComponent(channelGroup);

  useEffect(() => {
    if (saveStatus === AvtechApiStatus.SUCCESS) {
      setShowModal(false);
      dispatch(resetDeviceDataFormState());
    }
  }, [dispatch, saveStatus]);

  const handleSave = async (data) => {
    try {
      const updatedChannels = channelGroup.channels.map((channel) => {
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );
        let adjust = 0;
        if (data.channelData[channel.channelKey]
            && data.channelData[channel.channelKey].adjust !== undefined) {
          adjust = parseInt(data.channelData[channel.channelKey].adjust, 10);
        }
        const normalizedAdjust = adjustFromDisplay(
          channelInstance,
          adjust,
          preferencesDisplay.temperatureScale,
        );
        return {
          channelKey: trimRaaChannelKey(channel.channelKey),
          adjust: normalizedAdjust,
        };
      });
      const payload = {
        channelKey: channelGroup.channelKey,
        channels: updatedChannels,
        label: data.label,
        isHidden: data.isHidden,
      };
      await dispatch(updateSensor(payload)).unwrap();
      await dispatch(fetchSingleDevice()).unwrap();
      return true;
    } catch (error) {
      // Propagate the error to the modal component
      throw new Error(error.message || 'Failed to save sensor settings');
    }
  };

  // const getAlertChannel = (channelKey) => {
  //   const channel = alertChannels.find((alertChannel) => channelKey === alertChannel.channelKey);
  //   if (channel) {
  //     return channel.isTriggered;
  //   }
  //   return null;
  // };

  const handleCloseModal = () => {
    setShowModal(false);
    dispatch(resetDeviceDataFormState());
  };

  if (ReadingCardComponent) {
    const sortedChannels = [...channelGroup.channels]
      .filter((c) => {
        const { channelType } = parseChannelKey(c.channelKey);
        return channelType !== ChannelType.UNKNOWN.id && channelType !== ChannelType.PUSH_STATUS.id && channelType !== ChannelType.ALARM.id;
      })
      .sort((a, b) => {
        const aType = parseChannelKey(a.channelKey).channelType;
        const bType = parseChannelKey(b.channelKey).channelType;
        return (CHANNEL_TYPE_ORDER[aType] || Infinity) - (CHANNEL_TYPE_ORDER[bType] || Infinity);
      });
    let readingNumber = 1;
    const readings = sortedChannels.reduce((acc, channel) => {
      const parsedKey = parseChannelKey(channel.channelKey);
      const channelInstance = ChannelService.getChannelGroupChannel(
        parsedKey.channelGroupType,
        parsedKey.channelIndex,
        parsedKey.channelType,
      );
      if (parsedKey.channelGroupType === ChannelGroupType.EXTREME_TEMP.id && parsedKey.channelType === ChannelType.TEMPERATURE.id && parsedKey.channelIndex === 1) {
        const faultChannel = sortedChannels.find((ch) => parseChannelKey(ch.channelKey).channelType === ChannelType.EXTREME_TEMPERATURE_FAULT.id);
        if (faultChannel && faultChannel.value > 0) {
          return acc;
        }
      }
      const scale = channelInstance instanceof BaseTemperatureChannel ? preferencesDisplay.temperatureScale : preferencesDisplay.unitOfSpeed;
      if (parsedKey.channelType === ChannelType.ANALOG_COMPUTED.id) {
        acc[`reading${readingNumber}`] = {
          value: channelInstance.getDisplayValue(channel.value, { scale }),
          units: channelInstance.getUnits({ customUnits: channel.conversion.units }),
          title: channelInstance.displayName,
          isTriggered: null,
          channelKey: normalizeRaaChannelKey(channel.channelKey, activeSubscriptionId),
        };
      } else {
        acc[`reading${readingNumber}`] = {
          value: channelInstance.getDisplayValue(channel.value, { scale }),
          units: channelInstance.getUnits({ scale }),
          title: channelInstance.displayName,
          isTriggered: null,
          channelKey: normalizeRaaChannelKey(channel.channelKey, activeSubscriptionId),
        };
      }

      readingNumber += 1;
      return acc;
    }, {});

    const cgType = ChannelService.getChannelGroupTypeByValueId(parsedChannelKey.channelGroupType);
    const ModalComponent = sensorSettingsModalFactory(cgType.id);
    return (
      <div>
        <ReadingCardComponent
          sensorName={channelGroup.label}
          typeName={cgType.displayName}
          macAddress={parsedChannelKey.sensorMacAddress}
          lastUpdated={formatTimestamp(lastReceived, timezone, 'YYYY-MM-DD HH:mm:ss')}
          isWireless={false}
          isHub={false}
          onSettingsClick={isReadOnly ? () => {} : () => { setShowModal(true); }}
          isMuted={isMuted}
          {...readings}
        />
        <ModalComponent
          show={showModal}
          handleClose={handleCloseModal}
          sensorLabel={channelGroup.label}
          channelGroup={channelGroup}
          onSave={handleSave}
          isHidden={isHidden}
        />
      </div>
    );
  }
  return null;
};

DeviceSensorCard.propTypes = {
  channelGroup: PropTypes.shape({
    label: PropTypes.string.isRequired,
    channelKey: PropTypes.string.isRequired,
    channels: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.number.isRequired,
      channelKey: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  lastReceived: PropTypes.number.isRequired,
  isHidden: PropTypes.bool.isRequired,
  onToggleHidden: PropTypes.func.isRequired,
  isMuted: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
};

export default DeviceSensorCard;
